import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { snapshotsApi, Snapshot } from '@/lib/snapshots';
import { SiteHeader } from '@/components/layout/SiteHeader';
import { SiteFooter } from '@/components/layout/SiteFooter';
import { TotalsByPeriodChart } from '@/components/charts/TotalsByPeriodChart';
import { formatMoney } from '@/lib/currency';

interface DashSnapshot {
  workspaceId: string;
  scenarios: string[];
  accountCount: number;
  totalFacts: number;
  topAccounts: {
    account_code: string;
    scenario: string;
    total: number;
    points: { period_start: string; amount: number }[];
  }[];
  totalsByPeriod: { period_start: string; scenario: string; amount: number }[];
}

export function SharedSnapshotPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useQuery<Snapshot>({
    queryKey: ['snapshot', id],
    queryFn: () => snapshotsApi.getPublic(id!),
    enabled: !!id,
    retry: false,
  });

  return (
    <div className="min-h-screen bg-ink-50">
      <SiteHeader />
      <main className="mx-auto max-w-6xl px-6 py-10 space-y-6">
        {isLoading && <div className="text-ink-500 text-sm">{t('common.loading')}</div>}
        {error && (
          <div className="rounded-2xl border border-ink-200 bg-white p-12 text-center">
            <h1 className="text-2xl font-semibold text-ink-900">{t('common.not_found_title')}</h1>
            <p className="mt-1 text-sm text-ink-600">{t('common.not_found_sub')}</p>
          </div>
        )}
        {data && <SnapshotView snap={data} t={t} />}
      </main>
      <SiteFooter />
    </div>
  );
}

function SnapshotView({ snap, t }: { snap: Snapshot; t: (k: string, opts?: Record<string, unknown>) => string }) {
  const payload = snap.payload as DashSnapshot;
  const series = scenariosToSeries(payload);

  return (
    <>
      <div>
        <div className="inline-flex text-[10px] uppercase tracking-wide font-semibold text-accent-700 bg-accent-50 rounded-md px-2 py-0.5 mb-2">
          {t('workspace.share_snapshot')}
        </div>
        <h1 className="text-3xl font-semibold text-ink-900 tracking-tight">{snap.label}</h1>
        <p className="mt-1 text-ink-600 num text-xs">
          {new Date(snap.createdAt).toLocaleString()}
          {snap.expiresAt && <> · {new Date(snap.expiresAt).toLocaleDateString()}</>}
        </p>
      </div>

      <Tile title={t('workspace.totals_title')}>
        <TotalsByPeriodChart series={series} />
      </Tile>

      {payload.topAccounts && payload.topAccounts.length > 0 && (
        <Tile title={t('workspace.top_accounts', { count: payload.topAccounts.length })}>
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead>
              <tr className="text-left text-ink-500 border-b border-ink-100">
                <th className="py-2 pr-3 font-medium">{t('variance.th_account')}</th>
                <th className="py-2 px-3 font-medium">{t('common.scenario')}</th>
                <th className="py-2 px-3 font-medium text-right">{t('common.total')}</th>
              </tr>
            </thead>
            <tbody>
              {payload.topAccounts.map((a) => (
                <tr key={`${a.account_code}-${a.scenario}`} className="border-b border-ink-50">
                  <td className="py-2 pr-3 num font-medium text-ink-900">{a.account_code}</td>
                  <td className="py-2 px-3 text-ink-600">{a.scenario}</td>
                  <td className="py-2 px-3 num text-right font-medium text-ink-900">{formatMoney(a.total, { maximumFractionDigits: 0 })}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </Tile>
      )}
    </>
  );
}

function Tile({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-5">
      <h3 className="text-sm font-semibold text-ink-700 mb-3">{title}</h3>
      {children}
    </div>
  );
}

function scenariosToSeries(snap: { totalsByPeriod?: { period_start: string; scenario: string; amount: number }[] }) {
  if (!snap.totalsByPeriod) return [];
  const byScenario = new Map<string, { x: string; y: number }[]>();
  for (const point of snap.totalsByPeriod) {
    let arr = byScenario.get(point.scenario);
    if (!arr) {
      arr = [];
      byScenario.set(point.scenario, arr);
    }
    arr.push({ x: point.period_start, y: point.amount });
  }
  return Array.from(byScenario.entries()).map(([name, data]) => ({ name, data }));
}
